import { useMarketDataSnapshot } from '@src/hooks';
import { useCombinedMarketsAndAccounts } from '@src/hooks/useCombinedMarketsAndAccounts';
import { usePrimeFromOrderBook } from '@src/hooks/usePrimeFromOrderBook';
import { useDeepDiveContext } from '@src/providers/DeepDiveProvider';
import {
  Box,
  calculatePriceLadderDepth,
  MixpanelEvent,
  PriceLadder,
  UnifiedLiquidityEnum,
  useElementSize,
  useMixpanel,
  type OrderIntent,
  type SideEnum,
} from '@talos/kyoko';
import { useCallback, useEffect, useState } from 'react';

export function MobilePriceLadderModule() {
  const mixpanel = useMixpanel();
  const { security, selectedMarkets, selectedMarketAccounts, selectedAggregation, priceIncrement, setPriceIncrement } =
    useDeepDiveContext();
  const { elementRef, size } = useElementSize<HTMLDivElement>();
  const [depth, setDepth] = useState(10);

  const combinedMarketsAndAccounts = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);
  const requestedMarkets = combinedMarketsAndAccounts.length > 0 ? selectedMarkets : security?.Markets;

  const { marketDataSnapshots } = useMarketDataSnapshot(
    security?.Symbol
      ? {
          currency: security.BaseCurrency,
          markets: requestedMarkets,
          marketAccounts: selectedMarketAccounts,
          priceIncrement: priceIncrement ?? security.DefaultPriceIncrement,
          symbol: security.Symbol,
          depth,
          tag: 'MOBILE_SYMBOL_DEEP_DIVE_PRICE_LADDER',
          includeTradeDirection: false,
          unifiedLiquidity: UnifiedLiquidityEnum.Disabled,
        }
      : null
  );

  useEffect(() => {
    const height = size.offsetHeight;
    if (height && height > 0) {
      setDepth(calculatePriceLadderDepth(height));
    }
  }, [size.offsetHeight]);

  const primeFromOrderBook = usePrimeFromOrderBook({
    security,
    marketAccounts: selectedMarketAccounts,
    aggregation: selectedAggregation,
  });

  const handleClickRow = useCallback(
    (row: { side: SideEnum; price: string; size?: string; intent?: OrderIntent }) => {
      primeFromOrderBook({
        side: row.side,
        price: row.price,
        intent: row.intent,
        quantity: row.size,
      });
    },
    [primeFromOrderBook]
  );

  const handleIncrementChange = useCallback(
    (increment: string) => {
      setPriceIncrement(increment);
      mixpanel.track(MixpanelEvent.ChangeLadderIncrement);
    },
    [mixpanel, setPriceIncrement]
  );

  if (!security || marketDataSnapshots == null) {
    return null;
  }

  return (
    <Box ref={elementRef} w="100%" h="100%">
      <PriceLadder
        width={size.offsetWidth}
        increment={priceIncrement ?? security.DefaultPriceIncrement}
        onIncrementChange={handleIncrementChange}
        onClickRow={handleClickRow}
        security={security}
        steps={depth}
        marketDataObservable={marketDataSnapshots}
      />
    </Box>
  );
}
